// import type { Plugin } from 'vue'

import { ElMessage } from 'element-plus'

/**
 *
 * @param component 需要注册的组件
 * @param alias 组件别名
 * @returns any
 */
export const withInstall = <T>(component: T, alias?: string) => {
  const comp = component as any
  comp.install = (app: any) => {
    app.component(comp.name || comp.displayName, component)
    if (alias) {
      app.config.globalProperties[alias] = component
    }
  }
  return component as T & Plugin
}

/**
 * @param str 需要转下划线的驼峰字符串
 * @returns 字符串下划线
 */
export const humpToUnderline = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

/**
 * @param str 需要转驼峰的下划线字符串
 * @returns 字符串驼峰
 */
export const underlineToHump = (str: string): string => {
  if (!str) return ''
  return str.replace(/\-(\w)/g, (_, letter: string) => {
    return letter.toUpperCase()
  })
}

/**
 * 驼峰转横杠
 */
export const humpToDash = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

export const setCssVar = (prop: string, val: any, dom = document.documentElement) => {
  dom.style.setProperty(prop, val)
}

export const getCssVar = (prop: string, dom = document.documentElement) => {
  return getComputedStyle(dom).getPropertyValue(prop)
}

/**
 * 查找数组对象的某个下标
 * @param {Array} ary 查找的数组
 * @param {Functon} fn 判断的方法
 */
// eslint-disable-next-line
export const findIndex = <T = Recordable>(ary: Array<T>, fn: Fn): number => {
  if (ary.findIndex) {
    return ary.findIndex(fn)
  }
  let index = -1
  ary.some((item: T, i: number, ary: Array<T>) => {
    const ret: T = fn(item, i, ary)
    if (ret) {
      index = i
      return ret
    }
  })
  return index
}

export const trim = (str: string) => {
  return str.replace(/(^\s*)|(\s*$)/g, '')
}

/**
 * @param {Date | number | string} time 需要转换的时间
 * @param {String} fmt 需要转换的格式 如 yyyy-MM-dd、yyyy-MM-dd HH:mm:ss
 */
export function formatTime(time: Date | number | string, fmt: string) {
  if (!time) return ''
  else {
    const date = new Date(time)
    const o = {
      'M+': date.getMonth() + 1,
      'd+': date.getDate(),
      'H+': date.getHours(),
      'm+': date.getMinutes(),
      's+': date.getSeconds(),
      'q+': Math.floor((date.getMonth() + 3) / 3),
      S: date.getMilliseconds()
    }
    if (/(y+)/.test(fmt)) {
      fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length))
    }
    for (const k in o) {
      if (new RegExp('(' + k + ')').test(fmt)) {
        fmt = fmt.replace(
          RegExp.$1,
          RegExp.$1.length === 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length)
        )
      }
    }
    return fmt
  }
}

/**
 * 生成随机字符串
 */
export function toAnyString() {
  const str: string = 'xxxxx-xxxxx-4xxxx-yxxxx-xxxxx'.replace(/[xy]/g, (c: string) => {
    const r: number = (Math.random() * 16) | 0
    const v: number = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString()
  })
  return str
}

/**
 * 首字母大写
 */
export function firstUpperCase(str: string) {
  return str.toLowerCase().replace(/( |^)[a-z]/g, (L) => L.toUpperCase())
}

/**
 * 把对象转为formData
 */
export function objToFormData(obj: Recordable) {
  const formData = new FormData()
  Object.keys(obj).forEach((key) => {
    formData.append(key, obj[key])
  })
  return formData
}

/**
 *
 * @param enumlist 枚举列表
 * @param value 值
 */
export function formatFiledName(enumlist: formatFiledProps[], value: string | number) {
  let str = value
  enumlist.forEach((item) => {
    if (item.value == value) {
      str = item.label
    }
  })

  return str
}
/** 复制文本
 *  @param text 复制文本
 *
 *  */
export const copyText = (text) => {
  const textarea: any = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed' // 防止滚动到页面底部
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  try {
    const successful = document.execCommand('copy')
    const msg = successful ? '成功' : '失败'
    ElMessage.success(`复制文本到剪贴板 ${msg}`)
  } catch (err) {
    console.error('复制失败', err)
  }
  document.body.removeChild(textarea)
}
/** 下载
 * @param url 下载地址
 *  */
export const download = async (url) => {
  try {
    const fileUrl = url // 文件 URL
    const link = document.createElement('a')
    link.href = fileUrl
    link.download = '模版' // 下载的文件名
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    ElMessage.success('下载成功！')
  } catch (e) {
    ElMessage.success('下载失败！')
  }
}

/**
 * 导出文件
 * @param file 文件流
 */

export const exportFile = async (file: any) => {
  const fileName = window.decodeURI(file.headers['content-disposition'].split('=')[1])
  // 处理返回的文件流
  const blob = new Blob([file.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  /* 兼容 IE 内核，360 浏览器的兼容模式 */
  // @ts-ignore
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    // @ts-ignore
    window.navigator.msSaveOrOpenBlob(blob, fileName)
  } else {
    /* 其他浏览器（火狐、谷歌等）的文件下载方式 */
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.style.display = 'none'
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url) // 释放内存
    document.body.removeChild(link) // 清理 DOM
  }
}
/**
 * 解决两个数相加精度丢失问题
 * @param a
 * @param b
 * @returns {Number}
 */
export function floatAdd(a, b) {
  let c, d
  if (undefined == a || null == a || '' == a || isNaN(a)) {
    a = 0
  }
  if (undefined == b || null == b || '' == b || isNaN(b)) {
    b = 0
  }
  try {
    c = a.toString().split('.')[1].length
  } catch (f) {
    c = 0
  }
  try {
    d = b.toString().split('.')[1].length
  } catch (f) {
    d = 0
  }
  const e = Math.pow(10, Math.max(c, d))
  return (floatMul(a, e) + floatMul(b, e)) / e
}
/**
 * 解决两个数相减精度丢失问题
 * @param a
 * @param b
 * @returns {Number}
 */
export function floatSub(a, b) {
  let c, d
  if (undefined == a || null == a || '' == a || isNaN(a)) {
    a = 0
  }
  if (undefined == b || null == b || '' == b || isNaN(b)) {
    b = 0
  }
  try {
    c = a.toString().split('.')[1].length
  } catch (f) {
    c = 0
  }
  try {
    d = b.toString().split('.')[1].length
  } catch (f) {
    d = 0
  }
  const e = Math.pow(10, Math.max(c, d))
  return (floatMul(a, e) - floatMul(b, e)) / e
}
/**
 * 解决两个数相乘精度丢失问题
 * @param a
 * @param b
 * @returns {Number}
 */
export function floatMul(a, b) {
  let c = 0
  const d = a.toString()
  const e = b.toString()
  try {
    c += d.split('.')[1].length
  } catch (f) {}
  try {
    c += e.split('.')[1].length
  } catch (f) {}
  return (Number(d.replace('.', '')) * Number(e.replace('.', ''))) / Math.pow(10, c)
}
/**
 * 解决两个数相除精度丢失问题
 * @param a
 * @param b
 * @returns
 */
export function floatDiv(a, b) {
  let c,
    d,
    e = 0,
    f = 0
  try {
    e = a.toString().split('.')[1].length
  } catch (g) {}
  try {
    f = b.toString().split('.')[1].length
  } catch (g) {}
  return (
    (c = Number(a.toString().replace('.', ''))),
    (d = Number(b.toString().replace('.', ''))),
    floatMul(c / d, Math.pow(10, f - e))
  )
}
/**
 * 转化比例
 * @param rate 比例
 * @param type 转化类型
 * @returns 转化后的比例
 */
export const formateRate = (rate: number, type: 'to' | 'from' = 'to') => {
  if (rate == undefined || rate == null) return undefined
  return type === 'to' ? floatDiv(rate, 100) : floatMul(rate, 100)
}
/**
 * 去除小数后面的 0
 * @param val 小数字符串
 * @returns 转化后的小数
 */

export const delPaddingZero = (val) =>{
  if (typeof val !== 'string') val = String(val);
  
  // 1. 如果没有小数点，直接返回（除非你要去掉整数末尾的0，那另当别论）
  if (val.indexOf('.') === -1) return val;

  // 2. 去掉末尾的 0
  val = val.replace(/0+$/, '');

  // 3. 如果末尾变成了小数点，把小数点也去掉
  val = val.replace(/\.$/, '');

  // 4. 处理全为0的情况 (如 "0.000" -> "")，兜底返回 "0"
  return val === '' || val === '.' ? '0' : val;
}